import { useState } from 'react';

import { Button } from '@components';
import Modal from '@layouts/modal/Modal.v2';

import { MoveToCofeeAction, MoveToCofeeStep } from './constants';
import ModalBody from './ModalBody';
import Stepper from './Stepper';
import useMoveToCofeeForm from './useMoveToCofeeForm';
import useMoveToCofeeSubmit from './useMoveToCofeeSubmit';

import { cofeeNeutral10 } from '../../../../../themes/colors';
import { LeadType } from '../../types/common';
import { formatMobileNumber } from '../../utils/common';

interface MoveToCofeeModalProps {
  onClose: () => void;
  onSuccess: () => void;
  leadData: LeadType;
}

const MoveToCofeeModal = ({
  onClose,
  onSuccess,
  leadData
}: MoveToCofeeModalProps) => {
  const [currentStep, setCurrentStep] = useState<MoveToCofeeStep>(
    MoveToCofeeStep.CHOOSE_ACTION
  );
  const [selectedAction, setSelectedAction] =
    useState<MoveToCofeeAction | null>(null);
  const [nameApiError, setNameApiError] = useState('');

  const form = useMoveToCofeeForm({ leadName: leadData.name });

  // A validation error on the name field is returned by the API only after the
  // review step is submitted, so surface it back on the configure step.
  const handleNameError = (message: string) => {
    setNameApiError(message);
    setCurrentStep(MoveToCofeeStep.CONFIGURE);
  };

  // Clear the server-side name error as soon as the user edits the name.
  const clearNameErrorOnNameChange =
    <T extends { name?: string }>(update: (data: T) => void) =>
    (data: T) => {
      if (data.name !== undefined) setNameApiError('');
      update(data);
    };

  const { handleSubmit, isSubmitting } = useMoveToCofeeSubmit({
    leadData,
    selectedAction,
    memberListForm: form.memberListForm,
    quickCollectForm: form.quickCollectForm,
    quickCollectGroupForm: form.quickCollectGroupForm,
    onClose,
    onSuccess,
    onNameError: handleNameError
  });

  const handleContinue = () => {
    if (currentStep === MoveToCofeeStep.CHOOSE_ACTION && selectedAction) {
      setCurrentStep(MoveToCofeeStep.CONFIGURE);
    } else if (currentStep === MoveToCofeeStep.CONFIGURE) {
      setCurrentStep(MoveToCofeeStep.REVIEW);
    }
  };

  const handleGoBack = () => {
    if (currentStep === MoveToCofeeStep.CONFIGURE) {
      setCurrentStep(MoveToCofeeStep.CHOOSE_ACTION);
    } else if (currentStep === MoveToCofeeStep.REVIEW) {
      setCurrentStep(MoveToCofeeStep.CONFIGURE);
    }
  };

  const isReview = currentStep === MoveToCofeeStep.REVIEW;
  const showGoBack = currentStep !== MoveToCofeeStep.CHOOSE_ACTION;

  const description = `${leadData.name}  •  ${formatMobileNumber(
    leadData.mobile_number,
    leadData.country_code
  )}`;

  return (
    <Modal
      title="Move to CoFee"
      description={description}
      onClose={onClose}
      className="w-[631px] !rounded-2xl"
      descriptionClassOverride="!text-cofeeNeutral7"
      headerClassName="!text-cofeeNeutral10"
      bodyClassName="modal-scroll !pt-5 !pb-0"
      primaryButtonLabel={isReview ? 'Proceed' : 'Continue'}
      primaryButtonClick={isReview ? handleSubmit : handleContinue}
      primaryButtonDisabled={
        isReview
          ? isSubmitting
          : form.isContinueDisabled(currentStep, selectedAction)
      }
      primaryButtonLoading={isReview ? isSubmitting : false}
      secondaryButtonLabel="Cancel"
      secondaryButtonClick={onClose}
      secondaryButtonStyle="!h-11 !px-4"
      primaryButtonStyle="!h-11 !px-4"
      infoText={
        showGoBack ? (
          <Button
            variant="tertiary"
            label="Go Back"
            leftIconName="arrow-left"
            leftIconStroke={cofeeNeutral10}
            leftIconStyle="[&_path]:!stroke-cofeeNeutral10"
            labelStyle="!text-cofeeNeutral10 !font-medium"
            handleClick={handleGoBack}
          />
        ) : undefined
      }
    >
      <Stepper currentStep={currentStep} />
      <ModalBody
        currentStep={currentStep}
        selectedAction={selectedAction}
        onSelectAction={setSelectedAction}
        leadName={leadData.name}
        memberListForm={form.memberListForm}
        quickCollectForm={form.quickCollectForm}
        quickCollectGroupForm={form.quickCollectGroupForm}
        onMemberListChange={clearNameErrorOnNameChange(
          form.updateMemberListForm
        )}
        onQuickCollectChange={clearNameErrorOnNameChange(
          form.updateQuickCollectForm
        )}
        onQuickCollectGroupChange={clearNameErrorOnNameChange(
          form.updateQuickCollectGroupForm
        )}
        reviewFields={form.getReviewFields(selectedAction)}
        nameApiError={nameApiError}
      />
    </Modal>
  );
};

export default MoveToCofeeModal;
